// src/oura/merchant/HomeNumbers.tsx
//
// Home's numbers under the redesign (19 Oct 2026): three tiles, each one
// figure with the line that explains it and a link to the proof, then each AI
// assistant's ring with the biggest gap beside them.
//
// Recommended by AI and Found on Google read the same figures the
// switched-off tiles do (homeFigures.tsx). Your competitors puts the owner's
// own count beside the rival AI names most in their place, from the same
// reading as Visibility's Competitors tab, so the two cannot disagree.

import type { ReactNode } from "react";
import { ArrowLink, EngineLogo, SURFACE, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { OURA_ROOT, ouraHref } from "../nav";
import type { Figure } from "./homeFigures";
import { EngineRingRow } from "./HomeInsights";
import type { VisEngine } from "../visibility/model";
import type { MerchantLive } from "./model";

/** One figure, the line that says what it counts, and where the proof is. */
function NumberTile({ label, figure, note, link }: { label: string; figure: Figure; note: ReactNode; link: ReactNode }) {
    return (
        <section aria-label={label} className={cn(SURFACE.card, "flex flex-col gap-2 p-6")}>
            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>{label}</h2>
            <p className={cn(TYPE.h1, "m-0 mt-1 tabular-nums text-gray-900")}>
                {figure.loading ? <span className="inline-block h-9 w-24 animate-pulse rounded-md bg-gray-100 align-middle" /> : figure.value}
            </p>
            {figure.sub && !figure.loading && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{figure.sub}</p>}
            <p className={cn(TYPE.tb, "m-0 text-gray-500")}>{note}</p>
            <p className="m-0 mt-auto pt-2">{link}</p>
        </section>
    );
}

export function NumberTiles({
    found,
    visits,
    windowDays,
    onOpenVisits,
    live,
}: {
    found: Figure;
    visits: Figure;
    /** Search Console's window, when it reports one. */
    windowDays: number | null;
    /** Opens the searches behind the clicks; null when Google reports none. */
    onOpenVisits: (() => void) | null;
    live: MerchantLive | null | undefined;
}) {
    return (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3 [&>*]:min-w-0">
            <NumberTile
                label="Recommended by AI"
                figure={found}
                note="Questions where at least one AI names you."
                link={<ArrowLink to={`${OURA_ROOT}/questions`}>See every answer</ArrowLink>}
            />
            <NumberTile
                label="Found on Google"
                figure={visits}
                note={windowDays ? `Clicks from Google search in the last ${windowDays} days.` : "Clicks from Google search."}
                link={onOpenVisits ? <ArrowLink onClick={onOpenVisits}>See what people searched</ArrowLink> : <ArrowLink to={`${OURA_ROOT}/search`}>See what people searched</ArrowLink>}
            />
            <RivalTile live={live} />
        </div>
    );
}

/**
 * The owner's named count beside the rival named most in their place, as two
 * bars on one scale. Not measured says so; it is never two empty bars.
 */
function RivalTile({ live }: { live: MerchantLive | null | undefined }) {
    const found = live?.found ?? null;
    const top = live?.rivals?.[0] ?? null;
    const competitors = `${ouraHref("questions", { role: "oura-client" })}?tab=competitors`;
    const bar = (label: string, n: number, of: number, you: boolean) => (
        <div className="flex flex-col gap-1.5">
            <p className={cn(TYPE.tb, "m-0 flex justify-between gap-3", you ? "font-semibold text-gray-900" : "text-gray-700")}>
                <span className="min-w-0 truncate">{label}</span>
                <span className={cn("shrink-0 tabular-nums", !you && "text-gray-500")}>
                    {n} of {of}
                </span>
            </p>
            <span aria-hidden className="block h-2 overflow-hidden rounded-full bg-gray-100">
                <span className={cn("block h-full rounded-full", you ? "bg-blue-600" : "bg-gray-300")} style={{ width: `${of > 0 ? Math.round((n / of) * 100) : 0}%` }} />
            </span>
        </div>
    );
    let body: ReactNode;
    if (live === undefined) {
        body = <span className="mt-2 block h-16 animate-pulse rounded-xl bg-gray-100" />;
    } else if (!found || !live?.rivals) {
        body = <p className={cn(TYPE.tb, "m-0 text-gray-500")}>The businesses AI names in your place appear here after your first AI check.</p>;
    } else if (!top) {
        body = <p className={cn(TYPE.tb, "m-0 text-gray-500")}>On this check, no other business was named on a question that left you out.</p>;
    } else {
        const lead = found.named - top.on;
        body = (
            <>
                <div className="mt-2 flex flex-col gap-2.5">
                    {bar("You", found.named, found.of, true)}
                    {bar(top.name, top.on, found.of, false)}
                </div>
                <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>
                    {lead > 0 ? `You lead by ${lead}. ` : lead < 0 ? `${top.name} leads by ${-lead}. ` : "You are level. "}
                    On {top.takes} {top.takes === 1 ? "question" : "questions"}, AI names them and not you.
                </p>
            </>
        );
    }
    return (
        <section aria-label="Your competitors" className={cn(SURFACE.card, "flex flex-col gap-2 p-6")}>
            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Your competitors</h2>
            {body}
            <p className="m-0 mt-auto pt-2">
                <ArrowLink to={competitors}>{top ? `See those ${top.takes}` : "See competitors"}</ArrowLink>
            </p>
        </section>
    );
}

/**
 * Each AI assistant as Visibility draws it (EngineRingRow), with the one that
 * names them least beside the rings: the one Visibility's sentence names
 * (StandingReading.weakest), so the two pages name the same gap. With no
 * single weakest, the rings take the card.
 */
export function EnginesCard({ engines, weakest }: { engines: MerchantLive["engines"] | undefined; weakest: VisEngine | null }) {
    const gap = engines?.find((e) => e.key === weakest && e.named !== null && e.of !== null) ?? null;
    return (
        <section aria-labelledby="home-engines" className={cn(SURFACE.card, "flex flex-wrap items-center gap-x-8 gap-y-5 p-6")}>
            <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                    <h2 id="home-engines" className={cn(TYPE.tt, "m-0 text-gray-900")}>
                        Each AI assistant
                    </h2>
                    <ArrowLink to={`${OURA_ROOT}/questions`}>See Visibility</ArrowLink>
                </div>
                {engines === undefined ? (
                    <span className="block h-24 animate-pulse rounded-xl bg-gray-100" />
                ) : engines === null ? (
                    <p className={cn(TYPE.tb, "m-0 text-gray-500")}>Each assistant&rsquo;s figure appears after your first AI check.</p>
                ) : (
                    <EngineRingRow engines={engines} />
                )}
            </div>
            {gap && (
                <div className="flex min-w-0 flex-[1_1_300px] flex-col justify-center gap-1.5 self-stretch rounded-xl bg-gray-50 px-5 py-4">
                    <p className={cn(TYPE.lab, "m-0")}>Biggest gap</p>
                    <p className={cn(TYPE.tb, "m-0 flex items-center gap-2 font-semibold text-gray-900")}>
                        <EngineLogo engine={gap.key} size={16} />
                        <span>
                            {gap.label} names you on {gap.named} of {gap.of}.
                        </span>
                    </p>
                    <p className={cn(TYPE.tb, "m-0 text-gray-500")}>The assistant that names you least on this check.</p>
                </div>
            )}
        </section>
    );
}
